import { useToolSiteId } from "#app/sites/site-tool-context";
import { useAtomSet } from "@effect/atom-react";
import { reactivityInvalidateAtom } from "@crm/shared/query-atom";
import { useAISettings } from "@crm/ai/user-ai-credentials/use-ai-settings";
import { AiSettingsRpc } from "@crm/rpc-contract/groups/ai-settings";
import { clientCaughtCause, clientPromiseEffect } from "@crm/effect/client/promise-effect";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import type { AppError } from "@crm/errors/index";
import { Button } from "@crm/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@crm/ui/components/ui/card";
import { Input } from "@crm/ui/components/ui/input";
import { Label } from "@crm/ui/components/ui/label";
import { Textarea } from "@crm/ui/components/ui/textarea";
import { createFileRoute } from "@tanstack/react-router";
import { Effect, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { Eye, EyeOff, Loader2, Save, ShieldBan, Star, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { rpcCaller, rpcEffectCaller } from "@crm/rpc-client";
import { SioAiGenerationRpc, SioExternalLinkSettingsRpc } from "@crm/rpc-contract/groups/sio";
import type { ModelInfo } from "#app/sio/generator-types";
import { getModelListSource } from "#app/sio/model-list-service";
import { AVAILABLE_MODELS, getDefaultModel, isModelInList } from "#app/sio/models";
import {
  useSioExternalLinkSettings,
  useSioSettings,
  useUpdateSioModelSettings,
} from "#app/sio/use-sio-settings";
import { ModelSelect } from "../-components/model-select";
import { useSettingsStore } from "../-store/settings-store";

const aiSettingsFx = rpcEffectCaller(AiSettingsRpc);
const sioAiGeneration = rpcCaller(SioAiGenerationRpc);
const sioExternalLinkSettings = rpcCaller(SioExternalLinkSettingsRpc);

export const Route = createFileRoute("/_protected/sio-tools/_layout/settings")({
  head: () => ({}),
  component: SettingsPage,
});

function promiseEffect<A>(run: () => PromiseLike<A>) {
  return clientPromiseEffect("SIO 设置操作失败", run);
}

function rpcEffect<A, E>(effect: Effect.Effect<A, E>): Effect.Effect<A, AppError> {
  return effect.pipe(Effect.mapError((cause) => clientCaughtCause("SIO 设置操作失败", cause)));
}

function runSioSettingsRouteEffect<A>(effect: Effect.Effect<A, AppError>): Promise<A> {
  return browserEffectRuntime.runPromise(effect);
}

function errorDescription(error: unknown) {
  return error instanceof Error ? error.message : "未知错误";
}

const DOMAIN_INPUT_SEPARATOR_REGEX = /[\n,]+/;

function parseDomainInput(input: string): string[] {
  return input
    .split(DOMAIN_INPUT_SEPARATOR_REGEX)
    .map((item) => item.trim())
    .filter(Boolean);
}

function ExternalLinkSettingsCard() {
  const siteId = useToolSiteId();
  const invalidateExternalLinks = useAtomSet(reactivityInvalidateAtom);
  const [blacklistInput, setBlacklistInput] = useState("");
  const [preferredInput, setPreferredInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const settingsResult = useSioExternalLinkSettings();
  const settings = AsyncResult.value(settingsResult).pipe(Option.getOrUndefined);
  const isLoading = AsyncResult.isWaiting(settingsResult);

  useEffect(() => {
    setBlacklistInput(settings?.blacklist.map((entry) => entry.domain).join("\n") ?? "");
    setPreferredInput(settings?.preferredDomains.map((entry) => entry.domain).join("\n") ?? "");
  }, [settings?.blacklist, settings?.preferredDomains]);

  const handleSave = () => {
    setIsSaving(true);
    void runSioSettingsRouteEffect(
      Effect.gen(function* () {
        yield* promiseEffect(() =>
          sioExternalLinkSettings.updateSioExternalLinkSettings({
            siteId,
            blacklist: parseDomainInput(blacklistInput),
            preferredDomains: parseDomainInput(preferredInput),
          }),
        );
        yield* Effect.sync(() => invalidateExternalLinks(["sioExternalLinkSettings"]));
      }),
    )
      .then(() => {
        toast.success("外链引用设置已保存");
      })
      .catch((cause) => {
        toast.error("保存失败", {
          description: errorDescription(cause),
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>外链引用设置</CardTitle>
        <CardDescription>
          黑名单过滤搜索结果；优先域名会提升排序。输入支持完整 URL 或裸域名。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            正在加载外链设置...
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="flex items-center gap-2" htmlFor="external-link-blacklist">
                  <ShieldBan className="h-4 w-4" />
                  黑名单域名
                </Label>
                <Textarea
                  className="min-h-36 font-mono text-sm"
                  id="external-link-blacklist"
                  onChange={(event) => setBlacklistInput(event.target.value)}
                  placeholder="example.com&#10;https://vendor.example/path"
                  value={blacklistInput}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2" htmlFor="external-link-preferred">
                  <Star className="h-4 w-4" />
                  优先域名
                </Label>
                <Textarea
                  className="min-h-36 font-mono text-sm"
                  id="external-link-preferred"
                  onChange={(event) => setPreferredInput(event.target.value)}
                  placeholder="wikipedia.org&#10;who.int"
                  value={preferredInput}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-muted-foreground text-xs">
              <span>已保存：</span>
              <span>黑名单 {settings?.blacklist.length ?? 0}</span>
              <span>优先域名 {settings?.preferredDomains.length ?? 0}</span>
            </div>

            <Button disabled={isSaving} onClick={handleSave}>
              {isSaving ? <Loader2 className="animate-spin" /> : <Save size={16} />}
              保存外链引用设置
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── 主页面 ──────────────────────────────────────────────────────────────────

function SettingsPage() {
  const invalidateAiSettings = useAtomSet(reactivityInvalidateAtom);

  const aiSettingsResult = useAISettings();
  const aiSettings = AsyncResult.value(aiSettingsResult).pipe(Option.getOrUndefined);

  // SIO settings from DB
  const sioSettings = AsyncResult.value(useSioSettings()).pipe(Option.getOrUndefined);
  const storedAdsModel = sioSettings?.adsDefaultModel;
  const storedBlogModel = sioSettings?.blogDefaultModel;

  // Mutations for DB-backed settings
  const updateModelSettingsMutation = useUpdateSioModelSettings();

  // Cache-only store (localStorage)
  const {
    cachedModels: storedCachedModels,
    cachedModelsSource: storedCachedModelsSource,
    setCachedModels,
    clearBlockedModels,
  } = useSettingsStore();

  // === AI 配置本地状态 ===
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [adsModel, setAdsModel] = useState("");
  const [blogModel, setBlogModel] = useState("");
  const [isSavingConnection, setIsSavingConnection] = useState(false);
  const [isSavingModel, setIsSavingModel] = useState(false);
  const [isClearingCredential, setIsClearingCredential] = useState(false);

  // === 模型列表状态 ===
  const [apiModels, setApiModels] = useState<ModelInfo[]>(storedCachedModels);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  useEffect(() => {
    setBaseUrl(aiSettings?.aiBaseUrl ?? "");
    setAdsModel(storedAdsModel ?? aiSettings?.aiModel ?? getDefaultModel("ads"));
    setBlogModel(storedBlogModel ?? aiSettings?.aiModel ?? getDefaultModel("blog"));
  }, [aiSettings?.aiBaseUrl, aiSettings?.aiModel, storedAdsModel, storedBlogModel]);

  const savedBaseUrl = aiSettings?.aiBaseUrl?.trim() ?? "";
  const inputApiKey = apiKey.trim();
  const inputBaseUrl = baseUrl.trim();
  const savedModelSource = aiSettings?.configured
    ? getModelListSource(aiSettings.configVersion, savedBaseUrl || undefined)
    : null;
  const isConnectionSaved =
    (aiSettings?.configured ?? false) && !inputApiKey && inputBaseUrl === savedBaseUrl;

  const loadModelList = useCallback(
    (configVersion: number, targetBaseUrl?: string): Promise<ModelInfo[]> => {
      const source = getModelListSource(configVersion, targetBaseUrl);
      setIsLoadingModels(true);
      return runSioSettingsRouteEffect(
        Effect.gen(function* () {
          const models = yield* promiseEffect(() =>
            sioAiGeneration.fetchSavedSioModelList(undefined),
          );
          yield* Effect.sync(() => {
            setApiModels(models);
            setCachedModels(models, source);
          });
          return models;
        }),
      ).finally(() => {
        setIsLoadingModels(false);
      });
    },
    [setCachedModels],
  );

  useEffect(() => {
    if (!aiSettings?.configured) {
      setApiModels([]);
      if (storedCachedModels.length > 0 || storedCachedModelsSource !== null) {
        setCachedModels([], null);
      }
      return;
    }

    if (storedCachedModels.length > 0 && storedCachedModelsSource === savedModelSource) {
      setApiModels(storedCachedModels);
      return;
    }

    void loadModelList(aiSettings.configVersion, savedBaseUrl || undefined).catch(() => {
      setApiModels([]);
    });
  }, [
    aiSettings?.configured,
    aiSettings?.configVersion,
    savedBaseUrl,
    savedModelSource,
    setCachedModels,
    storedCachedModels,
    storedCachedModelsSource,
    loadModelList,
  ]);

  const handleSaveAiSettings = () => {
    if (!inputApiKey) {
      toast.error("请输入 API Key");
      return;
    }
    setIsSavingConnection(true);
    void runSioSettingsRouteEffect(
      Effect.gen(function* () {
        const result = yield* promiseEffect(() =>
          sioAiGeneration.testSioAIConnection({
            apiKey: inputApiKey,
            baseURL: inputBaseUrl || undefined,
          }),
        );
        if (!result.success) {
          return { status: "invalid" as const, result };
        }

        const updated = yield* rpcEffect(
          aiSettingsFx.updateAISettings({
            expectedVersion: aiSettings?.configVersion ?? 0,
            credentialMutation: { kind: "replace", value: inputApiKey },
            aiBaseUrl: inputBaseUrl || null,
          }),
        );
        yield* Effect.sync(() => {
          clearBlockedModels();
          setApiKey("");
          setShowApiKey(false);
          invalidateAiSettings(["aiSettings"]);
        });
        return { status: "saved" as const, result, settings: updated };
      }),
    )
      .then((outcome) => {
        if (outcome.status === "invalid") {
          toast.error(outcome.result.error || "API Key 验证失败", {
            description: outcome.result.message,
            duration: 8000,
          });
          return;
        }

        return loadModelList(
          outcome.settings.configVersion,
          outcome.settings.aiBaseUrl || undefined,
        ).then((models) => {
          const preferredModel = aiSettings?.aiModel ?? storedBlogModel ?? storedAdsModel;
          const fallbackModel = models[0]?.id ?? "";
          if (!isModelInList(adsModel, models)) {
            setAdsModel(
              preferredModel && isModelInList(preferredModel, models)
                ? preferredModel
                : fallbackModel,
            );
          }
          if (!isModelInList(blogModel, models)) {
            setBlogModel(
              preferredModel && isModelInList(preferredModel, models)
                ? preferredModel
                : fallbackModel,
            );
          }
          toast.success("连接已保存，模型列表已更新", {
            description: outcome.result.details
              ? `API 连接正常 (${outcome.result.details.duration})`
              : undefined,
          });
        });
      })
      .catch((cause) => {
        toast.error("保存失败", {
          description: errorDescription(cause),
        });
      })
      .finally(() => {
        setIsSavingConnection(false);
      });
  };

  const handleClearAiCredential = () => {
    if (!aiSettings?.configured) {
      return;
    }

    setIsClearingCredential(true);
    void runSioSettingsRouteEffect(
      Effect.gen(function* () {
        yield* rpcEffect(
          aiSettingsFx.updateAISettings({
            expectedVersion: aiSettings.configVersion,
            credentialMutation: { kind: "clear" },
          }),
        );
        yield* Effect.sync(() => {
          clearBlockedModels();
          setApiKey("");
          setShowApiKey(false);
          setApiModels([]);
          setCachedModels([], null);
          invalidateAiSettings(["aiSettings"]);
        });
      }),
    )
      .then(() => {
        toast.success("已清除保存的 API Key");
      })
      .catch((cause) => {
        toast.error("清除失败", { description: errorDescription(cause) });
      })
      .finally(() => {
        setIsClearingCredential(false);
      });
  };

  const handleSaveModelSettings = () => {
    if (!isConnectionSaved) {
      toast.error("请先保存并验证当前 API 连接");
      return;
    }
    if (!(adsModel && blogModel)) {
      toast.error("请选择广告和写文章默认模型");
      return;
    }
    if (!isModelInList(adsModel, apiModels)) {
      toast.error("广告文案默认模型不在服务商返回的可用列表中");
      return;
    }
    if (!isModelInList(blogModel, apiModels)) {
      toast.error("写文章默认模型不在服务商返回的可用列表中");
      return;
    }

    setIsSavingModel(true);
    void runSioSettingsRouteEffect(
      Effect.gen(function* () {
        const modelsToTest = [...new Set([adsModel, blogModel])];
        for (const model of modelsToTest) {
          const testResult = yield* promiseEffect(() =>
            sioAiGeneration.testSavedSioAIModelAvailability({ model }),
          );
          if (!testResult.success) {
            return {
              status: "unavailable" as const,
              model,
              testResult,
            };
          }
        }

        yield* rpcEffect(
          aiSettingsFx.updateAISettings({
            expectedVersion: aiSettings?.configVersion ?? 0,
            credentialMutation: { kind: "preserve" },
            aiModel: blogModel,
          }),
        );
        yield* promiseEffect(() =>
          updateModelSettingsMutation.executePromise({
            adsDefaultModel: adsModel,
            blogDefaultModel: blogModel,
          }),
        );
        yield* Effect.sync(() => invalidateAiSettings(["aiSettings"]));
        return { status: "saved" as const };
      }),
    )
      .then((outcome) => {
        if (outcome.status === "unavailable") {
          toast.error(`模型不可用：${outcome.model}`, {
            description: outcome.testResult.message,
            duration: 10_000,
          });
          return;
        }
        toast.success("默认模型已保存");
      })
      .catch((cause) => {
        toast.error("保存失败", {
          description: errorDescription(cause),
        });
      })
      .finally(() => {
        setIsSavingModel(false);
      });
  };

  const commonModels = AVAILABLE_MODELS;
  const canSelectModel = isConnectionSaved && apiModels.length > 0 && !isLoadingModels;

  return (
    <div className="notranslate container mx-auto px-4 py-8" translate="no">
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-8 font-bold text-3xl text-foreground">AI 配置</h1>

        {/* AI 配置区域 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>AI 配置</CardTitle>
            <CardDescription>
              配置你的 AI API 服务。支持 AIHubMix、OpenRouter 等 OpenAI 兼容
              API。配置会同步到云端，多设备共享。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="baseUrl">API Base URL</Label>
              <Input
                autoComplete="off"
                id="baseUrl"
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://aihubmix.com/v1 (默认)"
                value={baseUrl}
              />
              <p className="text-muted-foreground text-xs">
                留空则使用默认值。常见端点：
                <br />• AIHubMix:{" "}
                <code className="rounded bg-muted px-1 font-mono">https://aihubmix.com/v1</code>
                <br />• OpenRouter:{" "}
                <code className="rounded bg-muted px-1 font-mono">
                  https://openrouter.ai/api/v1
                </code>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    autoComplete="off"
                    className={`font-mono ${showApiKey ? "" : "text-transparent caret-transparent"}`}
                    id="apiKey"
                    onChange={(e) => setApiKey(e.target.value)}
                    onFocus={() => setShowApiKey(true)}
                    placeholder="输入你的 API Key (sk-...)"
                    value={apiKey}
                  />
                  {!showApiKey && apiKey && (
                    <div className="pointer-events-none absolute inset-0 flex items-center px-3 font-mono text-sm">
                      {"•".repeat(Math.min(apiKey.length, 40))}
                    </div>
                  )}
                </div>
                <Button onClick={() => setShowApiKey(!showApiKey)} size="icon" variant="outline">
                  {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              {aiSettings?.configured ? (
                <p className="text-muted-foreground text-xs">
                  已保存凭证 {aiSettings.maskedSuffix ?? ""}
                  。不会再次显示原始值。
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">尚未保存 API Key。</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button disabled={isSavingConnection} onClick={handleSaveAiSettings}>
                {isSavingConnection ? <Loader2 className="animate-spin" /> : <Save size={16} />}
                {isSavingConnection ? "验证中..." : "验证并保存连接"}
              </Button>
              {aiSettings?.configured && (
                <Button
                  disabled={isClearingCredential}
                  onClick={handleClearAiCredential}
                  variant="destructive"
                >
                  {isClearingCredential ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  清除已保存凭证
                </Button>
              )}
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label>广告文案默认模型</Label>
                <ModelSelect
                  allowCustom={false}
                  apiModels={apiModels}
                  commonModels={commonModels}
                  customValue=""
                  disabled={!canSelectModel}
                  isCustom={false}
                  onChange={setAdsModel}
                  placeholder={isConnectionSaved ? "搜索或选择模型..." : "先保存 API 连接"}
                  value={adsModel}
                />
              </div>

              <div className="space-y-2">
                <Label>写文章默认模型</Label>
                <ModelSelect
                  allowCustom={false}
                  apiModels={apiModels}
                  commonModels={commonModels}
                  customValue=""
                  disabled={!canSelectModel}
                  isCustom={false}
                  onChange={setBlogModel}
                  placeholder={isConnectionSaved ? "搜索或选择模型..." : "先保存 API 连接"}
                  value={blogModel}
                />
              </div>

              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                {isLoadingModels ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>正在加载模型列表...</span>
                  </>
                ) : isConnectionSaved && apiModels.length > 0 ? (
                  <span>已从当前服务商加载 {apiModels.length} 个模型</span>
                ) : isConnectionSaved ? (
                  <span>当前服务商没有返回可用模型，请重新验证连接</span>
                ) : (
                  <span>保存并验证连接后，才能选择模型</span>
                )}
              </div>
              <Button
                disabled={!canSelectModel || isSavingModel}
                onClick={handleSaveModelSettings}
                variant="outline"
              >
                {isSavingModel ? <Loader2 className="animate-spin" /> : <Save size={16} />}
                {isSavingModel ? "测试中..." : "测试并保存默认模型"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <ExternalLinkSettingsCard />
      </div>
    </div>
  );
}
